/**
 * This file contains the Host class, and also the necessary functions related to creating hosts.
 */

/**
 * Class Host
 *
 * @type {Object}
 */

import hostMethods from './host-methods';
import getID from './getID';
import { setCookie, getCookie } from './cookie-access-methods';

/**
 * Host
 */
class Host {

	/**
	 * constructor
	 */
	constructor( domain ) {
		this.allowedDomains = [];
		this.domain = domain;
	}

	/**
	 * Add host.
	 *
	 * @param {*} origin
	 * @param {*} methods
	 */
	addHost( origin, methods ) {
		this.allowedDomains.push( { origin: origin, allowedMethods: methods } );
	}

	/**
	 * Create connection for guests.
	 */
	connect() {
		this.storage = createHost( this.allowedDomains, this.domain );
	}

	/**
	 * Disconnect the storage.
	 */
	disconnect() {
		this.storage.close();
	}

}

export default Host;

/**
 * This function is responsible for creating host
 *
 * @param {*} allowedDomains The array of allowed domains
 * @param {*} hostDomain The current host's Domain (required to set the cookie)
 * @return {function} Returns functions like set, get, and clear.
 */
function createHost( allowedDomains, hostDomain ) {

	/**
	 * This function handles messages from postMessage events.
	 *
	 * @param {object} event The event object
	 * @return {void}
	 */
	function handleMessage( event ) {
		const { data } = event;
		const id = getID( data );
		
		if ( ! id ) {
			return;
		}
		
		const domain = allowedDomains.find( ( allowedDomain ) => {
			return event.origin === allowedDomain.origin;
		} );
		if ( ! domain ) {
			event.source.postMessage(
				{
					id,
					connectError: true,
					error: `${ event.origin } is not an allowed domain`
				},
				event.origin
			);

			return;
		}

		const { method } = data;

		if ( ! ~domain.allowedMethods.indexOf( method ) && 'connect' !== method ) {
			event.source.postMessage(
				{
					id,
					error: `${method} is not an allowed method from ${event.origin}`
				},
				event.origin
			);

			return;
		}

		hostMethods[method]( event, data, hostDomain );
	}

	/**
	 * This function closes the host connection.
	 *
	 */
	function close() {
		window.removeEventListener( 'message', handleMessage );
	}

	/**
	 * This function sets the host cookie value
	 *
	 * @param {string} key The cookie key
	 * @param {string} value The cookie value
	 */
	function set( key, value ) {
		setCookie( key, value, hostDomain );
	}

	/**
	 * This function gets the host cookie
	 *
	 * @param {string} key The cookie key
	 */
	function get( key ) {
		return getCookie( key );
	}

	window.addEventListener( 'message', handleMessage );

	return {
		set,
		get,
		close
	};
};
